import { useCallback } from 'react'
import { isValid } from 'date-fns/esm'
import { useField, useFormikContext } from 'formik'

import type { MuiErrorState, SetMuiErrorState } from './typings'
import { dateErrorToString } from './utils'
import type { FiltersFormModel } from '../../formConfig'
import { DateValidationError } from '@mui/x-date-pickers/models/validation'

type Props = {
  field: 'start' | 'end'
  muiErrors: MuiErrorState
  setMuiErrors: SetMuiErrorState
}

export function useDateRangeInput(props: Props) {
  const { field, muiErrors, setMuiErrors } = props

  const name = field === 'start' ? 'startDate' : 'endDate'

  const { setFieldError, setFieldValue } = useFormikContext<FiltersFormModel>()

  const [{ value, onBlur }, { error }] = useField<Date | null>({
    name,
    validate: (v) => {
      if (v === null) return 'Required'
      if (muiErrors[field]) return muiErrors[field]
      if (!isValid(v)) return 'Invalid Date'
    },
  })

  const onError = useCallback(
    (error: DateValidationError, _value: Date | null) => {
      const errorString = dateErrorToString(error)
      setMuiErrors((e) => ({ ...e, [field]: errorString }))
      setFieldError(name, errorString)
    },
    [field, name, setFieldError, setMuiErrors],
  )

  const onChange = useCallback(
    (date: Date | null) => setFieldValue(name, date, false),
    [name, setFieldValue],
  )

  const onAccept = useCallback(
    (date: Date | null) => setFieldValue(name, date),
    [name, setFieldValue],
  )

  return {
    value,
    error,
    onBlur,
    onError,
    onAccept,
    onChange,
  }
}
